// ─────────────────────────────────────────────────────────────────────────
// Client half of the card-on-file purchase flow (PANDA-1383).
//
// The purchase routes answer in one of two shapes:
//   { url }      → no saved card: go to hosted Stripe Checkout, as before.
//   { charged }  → saved card: the server already took the money and granted
//                  the plan/credits. There is nothing to redirect to.
//
// Every caller must handle BOTH. A caller that only looks for `url` would show
// "couldn't start checkout" to a user who has just been successfully charged —
// so this helper exists to make the charged case impossible to forget.
// ─────────────────────────────────────────────────────────────────────────

export type PurchaseResponse = {
  charged?: boolean;
  url?: string;
  error?: string;
  code?: string;
};

/**
 * Handle a purchase that completed on the saved card. Returns true when it did,
 * meaning the caller is done — check this BEFORE looking at `url`.
 *
 * We still navigate, to `dest` + the same success flag the Stripe redirect would
 * have come back with, because that is what makes the host page re-read credits
 * and render the new plan. There is deliberately no `session_id`: that param
 * exists to trigger the reconcile-on-return webhook fallback, and a direct
 * charge was already fulfilled server-side — asking the page to reconcile a
 * Checkout session that never existed would 4xx.
 */
export function completedOnSavedCard(
  data: PurchaseResponse | null | undefined,
  dest: string,
  flag: "upgraded" | "topup" = "upgraded",
): boolean {
  if (!data?.charged) return false;
  const sep = dest.includes("?") ? "&" : "?";
  window.location.assign(`${dest}${sep}${flag}=true`);
  return true;
}
